'use client';

import { useState, useEffect } from 'react';

interface ContactFormProps {
  language: 'en' | 'es';
}

const ContactForm = ({ language }: ContactFormProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    timeline: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('contact-form');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const content = {
    en: {
      name: 'Name',
      email: 'Email',
      service: 'What do you need?',
      timeline: 'Timeline?',
      message: 'Brief context',
      submit: 'Send',
      serviceOptions: [
        { value: '', label: 'Select a service' },
        { value: 'immigration', label: 'Immigration' },
        { value: 'estate', label: 'Estate Planning' },
        { value: 'corporate', label: 'Corporate' },
        { value: 'other', label: 'Other' },
      ],
      timelineOptions: [
        { value: '', label: 'Select timeline' },
        { value: 'asap', label: 'ASAP' },
        { value: 'month', label: 'This month' },
        { value: 'quarter', label: 'This quarter' },
        { value: 'exploring', label: 'Just exploring' },
      ],
    },
    es: {
      name: 'Nombre',
      email: 'Email',
      service: '¿Qué necesitas?',
      timeline: '¿Para cuándo?',
      message: 'Contexto breve',
      submit: 'Enviar',
      serviceOptions: [
        { value: '', label: 'Selecciona un servicio' },
        { value: 'immigration', label: 'Inmigración' },
        { value: 'estate', label: 'Planificación Patrimonial' },
        { value: 'corporate', label: 'Corporativo' },
        { value: 'other', label: 'Otro' },
      ],
      timelineOptions: [
        { value: '', label: 'Selecciona plazo' },
        { value: 'asap', label: 'Lo antes posible' },
        { value: 'month', label: 'Este mes' },
        { value: 'quarter', label: 'Este trimestre' },
        { value: 'exploring', label: 'Solo explorando' },
      ],
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    alert(language === 'en' ? 'Message sent! We will respond within 24 hours.' : '¡Mensaje enviado! Responderemos en 24 horas.');
    
    setFormData({
      name: '',
      email: '',
      service: '',
      timeline: '',
      message: '',
    });
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact-form" className="py-20 px-6">
      <div className="max-w-2xl mx-auto">
        <form
          onSubmit={handleSubmit}
          className={`space-y-6 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
        >
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
              {content[language].name}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-input border hairline border-border sharp text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
              {content[language].email}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-input border hairline border-border sharp text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Service */}
          <div>
            <label htmlFor="service" className="block text-sm font-semibold text-foreground mb-2">
              {content[language].service}
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-input border hairline border-border sharp text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {content[language].serviceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Timeline */}
          <div>
            <label htmlFor="timeline" className="block text-sm font-semibold text-foreground mb-2">
              {content[language].timeline}
            </label>
            <select
              id="timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-input border hairline border-border sharp text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {content[language].timelineOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
              {content[language].message}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className="w-full px-4 py-3 bg-input border hairline border-border sharp text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 text-base font-semibold text-primary-foreground bg-primary sharp hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? '...' : content[language].submit}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;